import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';
import type { KanbanBoard, KanbanCard } from '@pm/shared';
import { apiErrorMessage } from '../../lib/apiErrorMessage';

/**
 * PHQ-104: Projekt-Boards (Aufgaben direkt am Projekt, `ap_id` = null).
 * Pro Projekt höchstens ein aktives Board, ältere sind eingefroren (nur lesen).
 * Karten-Routen (anlegen/ändern/löschen) sind dieselben wie beim AP-Board.
 */
const KEY = (pid: string) => ['projects', pid, 'kanban', 'project-boards'] as const;

/** Board ohne Arbeitspaket-Bindung. Das gemeinsame `KanbanBoard`-Typmodell kennt `ap_id`
 *  nur als Pflichtfeld; die Darstellung nutzt `ap_id` nicht, daher die Zuordnung am Rand. */
export type ProjektKanbanBoard = KanbanBoard;
export type ProjektKanbanCardData = Partial<Pick<KanbanCard, 'state' | 'title' | 'description' | 'person' | 'hidden' | 'seq'>>
  & { estimated_minutes?: number | null };

/** Boards des Projekts, absteigend nach seq: [0] = aktiv. */
export function useProjektBoards(projectId: string | undefined) {
  return useQuery({
    queryKey: KEY(projectId ?? ''),
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/kanban/project-boards`),
    enabled: !!projectId,
  });
}

export function unwrapProjektBoards(resp: unknown): ProjektKanbanBoard[] {
  return (resp as { data?: ProjektKanbanBoard[] } | undefined)?.data ?? [];
}

/** „Neue Woche": friert das aktive Board ein, das neue übernimmt die Karten (server-side). */
export function useCreateProjektBoard(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post(`/api/v1/projects/${pid}/kanban/project-boards`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(pid) }),
  });
}

export function useRenameProjektBoard(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { boardId: string; label: string }) =>
      apiClient.patch(`/api/v1/projects/${pid}/kanban/boards/${vars.boardId}`, { label: vars.label }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(pid) }),
  });
}

/* Die folgenden Karten-Hooks haben bewusst dieselbe Variablenform wie useKanban.ts
 * (`apId` wird ignoriert), damit KanbanBoardView beide Varianten austauschbar nutzt. */
export function useCreateProjektCard(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { apId?: string | null; boardId: string; data: ProjektKanbanCardData }) =>
      apiClient.post(`/api/v1/projects/${pid}/kanban/boards/${vars.boardId}/cards`, vars.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(pid) }),
  });
}

export function useUpdateProjektCard(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { apId?: string | null; cardId: string; data: ProjektKanbanCardData }) =>
      apiClient.patch(`/api/v1/projects/${pid}/kanban/cards/${vars.cardId}`, vars.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(pid) }),
  });
}

export function useDeleteProjektCard(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { apId?: string | null; cardId: string }) =>
      apiClient.delete(`/api/v1/projects/${pid}/kanban/cards/${vars.cardId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(pid) }),
  });
}

/** Verständliche Meldung: 409 = Board ist eingefroren (inzwischen eine neue Woche begonnen). */
export function projektKanbanFehler(err: unknown, fallback: string): string {
  if ((err as { status?: number } | null)?.status === 409) {
    return 'Dieses Board ist eingefroren und kann nicht mehr geändert werden. Bitte die Ansicht neu laden und am aktiven Board weiterarbeiten.';
  }
  return apiErrorMessage(err, fallback);
}
